import { Component, OnInit, ChangeDetectorRef, inject, ViewChild, OnDestroy, TemplateRef, ElementRef } from "@angular/core";
import { CommonModule } from '@angular/common';
import { HostListener } from '@angular/core';
import { Router, RouterOutlet, RouterLink, NavigationEnd, ActivatedRoute } from "@angular/router";
import { FormControl } from '@angular/forms';
import { EntityChooserDialog } from '../entity-chooser/entity-chooser.component';
import { ProfileDialogComponent } from '../profile-dialog/profile-dialog.component';
import { environment } from '../../../environments/environment';

import { AgGridModule } from 'ag-grid-angular';

import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatGridListModule } from "@angular/material/grid-list";
import { MatCardModule } from "@angular/material/card";
import { TitleService } from "../../service/title.service";
import { filter, Subscription } from "rxjs";
import { MatSidenav, MatSidenavContainer, MatSidenavModule } from "@angular/material/sidenav";
import { SidenavContentService } from "../../service/sidenav-content.service";
import { AuthService } from "../../service/auth.service";
import { PermissionsStoreService } from "../../service/permissions/permissions-store.service";
import { ConciergeDialogComponent } from "../../shared/components/concierge-dialog/concierge-dialog.component";
import { ConciergeUsageService } from "../../service/concierge/concierge-usage.service";


@Component({
    selector: 'secure-home',
    templateUrl: './secureHome.html',
    styleUrls: ['./secureHome.css'],
    standalone: true,
    imports: [
      CommonModule,
      AgGridModule,
      RouterOutlet,
      RouterLink,
      MatMenuModule,
      MatIconModule,
      MatToolbarModule,
      MatGridListModule,
      MatCardModule,
      MatSidenavModule,
    ]
  })
export class SecureHomeComponent implements OnInit, OnDestroy {
    public titleService = inject(TitleService);
    private activatedRoute = inject(ActivatedRoute);
    public sidenavContentService = inject(SidenavContentService);
    private authService = inject(AuthService);
    public permissionsStore = inject(PermissionsStoreService);
    // Injected purely to construct it at landing, so its email-scoped localStorage
    // load is already done before the first Cmd-K. Without this it constructs on
    // first palette open and the "Frequently used" section pops in a beat late.
    private conciergeUsage = inject(ConciergeUsageService);
    currentSidenavTemplate: TemplateRef<any> | null = null;
    private sidenavContentSubscription!: Subscription;

    @ViewChild('sidenav') sidenav!: MatSidenav;
    @ViewChild('sidenav', { read: ElementRef }) sidenavElement!: ElementRef;


    searchTerm = new FormControl('');

    userManagementRole: any;

    opened = true;

    documentReviewList = environment.documentReviewList;
    workflowManagement = environment.workflowManagement;
    provisions = (environment as any).provisions === true;
    conciergeNavigator = (environment as any).conciergeNavigator === true;
    timesheets = environment.timesheets;
    timecards = (environment as any).timecards === true;
    assessments = environment.assessments;

    allTimesheetSpecs = null;
    usersAllowedTimesheets: string[] = [];
    timesheetsAllowed = environment.timesheets;
    timecardsAllowed = (environment as any).timecards === true;

    private closeSidenavSubscription!: Subscription;
    private toggleSidenavSubscription!: Subscription;
    private detailSidenavSubscription!: Subscription;
    detailSidenavActive = false;


    constructor(public router: Router,
                public entityChooser: MatDialog,
                private ref: ChangeDetectorRef ) 
      {}

    async ngOnInit() {

      // If a hard session-expiry stashed where the user was, return them there
      // after re-login (one-shot).
      const back = sessionStorage.getItem('post_login_redirect');
      if (back) {
        sessionStorage.removeItem('post_login_redirect');
        if (back !== this.router.url) {
          this.router.navigateByUrl(back);
        }
      }

      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        let currentRoute = this.activatedRoute.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        if (currentRoute.snapshot.data['title']) {
          this.titleService.setTitle(currentRoute.snapshot.data['title']);
        }
      });

      this.sidenavContentSubscription = this.sidenavContentService.currentSidenavContent.subscribe(template => {
        this.currentSidenavTemplate = template;
      });

      // Subscribe to sidenav close requests
      this.closeSidenavSubscription = this.sidenavContentService.closeSidenav$.subscribe(shouldClose => {
        if (shouldClose && this.sidenav?.opened) {
          this.sidenav.close();
        }
      });

      // Subscribe to sidenav toggle requests (for main sidenav when no detail view is active)
      this.toggleSidenavSubscription = this.sidenavContentService.toggleSidenav$.subscribe(() => {
        this.sidenav?.toggle();
      });

      // Subscribe to detail sidenav active state to hide/show main sidenav
      this.detailSidenavSubscription = this.sidenavContentService.detailSidenavActive$.subscribe(isActive => {
        this.detailSidenavActive = isActive;
        if (isActive && this.sidenav?.opened) {
          this.sidenav.close();
        }
      });

      // Load the signed-in user's role + activation state from {org}-Users into the
      // permissions store. Drives admin-only UI affordances (Proxy Assignments nav
      // link, etc.) — does not gate writes; the backend authoritatively enforces
      // admin-role checks. Uses ensureLoaded() so it reuses the fetch the auth guard
      // already performed for the activation gate (no double request).
      const email = await this.authService.getUserEmail();
      if (email) {
        await this.permissionsStore.ensureLoaded(email);
        this.promptForNameIfMissing();
      }

      // First launch after login: greet the user by putting the "Ask Me" concierge
      // in front of them. SecureHome mounts once per login, so this fires once (not
      // on every in-app navigation). Flag-gated, and skipped if another dialog (e.g.
      // the SSO name prompt) is already open so we don't stack modals.
      if (this.conciergeNavigator) {
        setTimeout(() => {
          if (this.entityChooser.openDialogs.length === 0) {
            this.openConcierge(true);
          }
        }, 350);
      }
    }

    /**
     * SSO users coming in from Crestwood may have no first/last name (the SAML
     * assertion didn't carry given_name/family_name). When the loaded {org}-Users
     * record is missing either name, prompt them to fill it in via the Profile
     * dialog (which saves to {org}-Users). The dialog is dismissable; because the
     * trigger is simply "name missing on the record," dismissing it just means it
     * re-appears on the next login until they actually save a name — no separate
     * "dismissed" flag needed. Gated to SSO so the username/password / local-test
     * flow isn't prompted.
     */
    private promptForNameIfMissing(): void {
      if (!environment.sso) {
        return;
      }
      const record = this.permissionsStore.permissions();
      const firstName = (record?.firstName ?? '').trim();
      const lastName = (record?.lastName ?? '').trim();
      if (!firstName || !lastName) {
        this.openProfileDialog();
      }
    }

    ngOnDestroy() {
      if (this.sidenavContentSubscription) {
        this.sidenavContentSubscription.unsubscribe();
      }
      if (this.closeSidenavSubscription) {
        this.closeSidenavSubscription.unsubscribe();
      }
      if (this.toggleSidenavSubscription) {
        this.toggleSidenavSubscription.unsubscribe();
      }
      if (this.detailSidenavSubscription) {
        this.detailSidenavSubscription.unsubscribe();
      }
    } 

    // Global command palette / concierge. Cmd-K (mac) / Ctrl-K (win) opens the
    // natural-language navigator. Gated on the conciergeNavigator env flag and
    // scoped to /securehome/* (this component only mounts there). Guards against
    // opening a second instance.
    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent) {
      if (!this.conciergeNavigator) return;
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        this.openConcierge();
      }
    }

    /**
     * Opens the concierge / command palette (the "✨ Ask Me ✨" toolbar button
     * and the Cmd-K shortcut both call this). Guards against a second instance.
     * `greet` = the login welcome open: shows the "What do you want to do today?"
     * greeting (no autofocus) instead of jumping straight to a focused, empty box.
     */
    openConcierge(greet: boolean = false) {
      if (!this.conciergeNavigator) return;
      const alreadyOpen = this.entityChooser.openDialogs.some(
        d => d.componentInstance instanceof ConciergeDialogComponent
      );
      if (alreadyOpen) return;
      this.entityChooser.open(ConciergeDialogComponent, {
        width: '640px',
        maxWidth: '92vw',
        position: { top: '96px' },
        panelClass: 'concierge-dialog-panel',
        autoFocus: false,
        data: { greet },
      });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
      // Check if the sidenav exists and is open
      if (this.sidenav?.opened) {
        // Check if the click happened outside the sidenav's native element
        const clickedElement = event.target as HTMLElement;
        if (this.sidenavElement && !this.sidenavElement.nativeElement.contains(clickedElement)) {
          this.sidenav.close();
        }
      }
    }


    openHelpInNewTab() {
      window.open('https://www.healthycontracts.com/hci-user-manuals-guides', "_blank");
    }

    openProfileDialog() {
      this.entityChooser.open(ProfileDialogComponent, {
        width: '450px',
        disableClose: false
      });
    }

    // This is because we change the title of the Toolbar
    ngAfterContentChecked() {
            this.ref.detectChanges();
    }


    // toggleOpened() {
    // }


    openEntityChooserDialog(): void {
        const dialogRef = this.entityChooser.open(EntityChooserDialog, {
          width: '400px',
          data: {},
          position: {
              top: '70px',
              right: '30px'
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          console.log('The dialog was closed');
          console.log('entity selected: ', result);
        });
      }



    newContractForm() {
        console.log('Routing to new contract form. ');
        this.router.navigateByUrl('/securehome/new-contract');
    }
}